import { useTranslation } from 'next-i18next'
import { forwardRef, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { ActivityProps, InputsAnswer } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import Input from '~/bootcamp/Activities/components/Input'
import Equals from '~/bootcamp/Activities/components/Equals'
import Times from '~/bootcamp/Activities/components/Times'
import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import useFactorDivision from '~/bootcamp/Activities/Factorization/hooks/useFactorDivision'
import { generateInitialAnswers, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import useFocus from '../../hooks/useFocus'

const FactorDivision = ({
  difficultyLevel,
  onNextQuestionClick,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitAnswer,
  autofocus = true,
}: ActivityProps): React.ReactElement => {
  const [quotientInputs, setQuotientInputs] = useState<InputsAnswer>({})
  const [primeInputs, setPrimeInputs] = useState<InputsAnswer>({})
  const [answerInputs, setAnswerInputs] = useState<InputsAnswer>({})
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const [questionNumber, numberOfInputs, quotientNumbersList, generateNewQuestion, verifyAnswer] =
    useFactorDivision(difficultyLevel)

  const inputFieldIterators = useMemo(() => Array.from({ length: numberOfInputs }, (_, i) => i + 1), [numberOfInputs])

  useEffect(() => {
    const initialAnswers = generateInitialAnswers(inputFieldIterators)

    setAnswerInputs(initialAnswers)
  }, [inputFieldIterators])

  useEmitInstance(
    onInstanceChange,
    questionNumber ? `${questionNumber} = ${Array(numberOfInputs).fill('\\square').join(' \\cdot ')}` : null,
  )

  const checkAnswer = (): void => {
    const answer = Object.keys(answerInputs).map((key) => parseFloat(answerInputs[key]))
    const isCorrect: boolean = verifyAnswer(answer)
    onAnswerStateChange(isCorrect)
  }

  const nextQuestion = (): void => {
    generateNewQuestion()
    clearAnswer()
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (isFormValid()) {
      hocOnEnterPress(answerState, checkAnswer, nextQuestion)
    }
  }

  const clearAnswer = (): void => {
    const resetAnswers = generateInitialAnswers(inputFieldIterators)
    setAnswerInputs(resetAnswers)

    const resetPrimeInputs = generateInitialAnswers(inputFieldIterators, 'prime')
    setPrimeInputs(resetPrimeInputs)

    const resetQuotientInputs = generateInitialAnswers(inputFieldIterators, 'quotient')
    setQuotientInputs(resetQuotientInputs)

    onAnswerStateChange(null)
  }

  const quotientsList = (): React.ReactElement => {
    return (
      <Fragment>
        {quotientNumbersList.map((num) => (
          <NumberBox key={num}>{num}</NumberBox>
        ))}
      </Fragment>
    )
  }

  const handleQuotientInputChange = (value: string, name: string): void => {
    setQuotientInputs((existingInput) => ({ ...existingInput, [name]: value }))
  }

  const quotientInputsList = (): React.ReactElement => {
    return (
      <Fragment>
        {quotientNumbersList.map((_, i) => {
          const fieldName = `quotient_${i + 1}`
          // eslint-disable-next-line react/no-array-index-key
          return (
            <InputBox
              key={fieldName}
              name={fieldName}
              onChange={handleQuotientInputChange}
              value={quotientInputs[fieldName]}
            />
          )
        })}
      </Fragment>
    )
  }

  const handlePrimeInputChange = (value: string, name: string): void => {
    setPrimeInputs((existingInput) => ({ ...existingInput, [name]: value }))
  }

  const primeInputsList = (): React.ReactElement => {
    return (
      <Fragment>
        {inputFieldIterators.map((arrInd) => {
          const fieldName = `prime_${arrInd}`

          return (
            <InputBox
              key={fieldName}
              name={fieldName}
              onChange={handlePrimeInputChange}
              ref={arrInd === 1 ? inputRef : null}
              value={primeInputs[fieldName]}
            />
          )
        })}
      </Fragment>
    )
  }

  const handleAnswerInputChange = (value: string, name: string): void => {
    onAnswerStateChange(null)
    setAnswerInputs((existingInput) => ({ ...existingInput, [name]: value }))
  }

  const answerInputsList = (): React.ReactElement => {
    return (
      <Fragment>
        {inputFieldIterators.map((arrInd, index) => {
          const fieldName = `answer_${arrInd}`
          return (
            <Fragment key={fieldName}>
              <InputBox
                autoFocus={autofocus && index === 0}
                className="w-auto md:w-auto"
                isError={answerState === false}
                key={`${inputKey}-${fieldName}`}
                name={fieldName}
                onChange={handleAnswerInputChange}
                onEnter={onEnterPress}
                value={answerInputs[fieldName]}
              />

              {arrInd !== numberOfInputs && <Times />}
            </Fragment>
          )
        })}
      </Fragment>
    )
  }

  const isFormValid = (): boolean => {
    return !Object.keys(answerInputs).some((key) => answerInputs[key] === '')
  }

  return (
    <ActivityWrapper>
      <div className="md:min-h-xs flex justify-center">
        <div className="w-14 md:w-20 min-h-inherit">
          <NumberBox>{questionNumber}</NumberBox>
          {difficultyLevel > 2 ? quotientInputsList() : quotientsList()}
        </div>
        <div className="w-1 bg-purple rounded-full min-h-inherit" />
        <div className="w-14 md:w-20 min-h-inherit">{primeInputsList()}</div>
      </div>

      <div className="flex items-center justify-center mt-9">
        <NumberBox className="w-auto md:w-auto">{questionNumber}</NumberBox>
        <Equals />
        {answerInputsList()}
      </div>

      {SubmitAnswer && (
        <SubmitAnswer
          clearFormAction={clearAnswer}
          correctAnswer={!!answerState}
          formValid={isFormValid()}
          goToNextQuestionAction={nextQuestion}
          submitAnswerAction={checkAnswer}
        />
      )}
    </ActivityWrapper>
  )
}

const NumberBox = ({ children, className = '' }: { children: ReactNode; className?: string }): React.ReactElement => {
  return (
    <div className={`text-center w-14 md:w-20 h-14 md:h-20 flex flex-col justify-center ${className}`}>
      <p className="text-purple md:text-lg font-semibold">{children}</p>
    </div>
  )
}

interface InputBoxProps {
  name: string
  onChange: (value: string, name: string) => void
  value: string
  className?: string
  autoFocus?: boolean
  isError?: boolean
  onEnter?: () => void
}

// eslint-disable-next-line react/display-name
const InputBox = forwardRef<HTMLInputElement | null, InputBoxProps>((props, ref): React.ReactElement => {
  const { t } = useTranslation('generics')
  const { name, onChange, value = '', className = '', isError, autoFocus, onEnter } = props

  return (
    <div className={`text-center w-14 md:w-20 h-14 md:h-20 flex flex-col justify-center items-center ${className}`}>
      <Input
        aria-label={t('ariaLabelButtons.insertValue')}
        autoFocus={autoFocus}
        isError={isError}
        maxDigits={4}
        name={name}
        onChange={onChange}
        onEnter={onEnter}
        ref={ref}
        value={value}
      />
    </div>
  )
})

export default FactorDivision
